import type { Site } from "@fts/core";
import { useI18n } from "../i18n";
import { canLiveWatchSite } from "../lib/permissions";
import { useSession } from "../state/session";
import { formatCountdown, useLiveWatch } from "../state/liveWatch";
import { Icon } from "./Icon";

/**
 * "Canlı izle" (ADR 0014): asks the vehicle's device to report every second for 10 minutes. Shows
 * "waiting for the device" until the device confirms, then the time left. Visible to users who may
 * manage the vehicle's site as safety officer or above, only for vehicles with a device. The database
 * re-checks the rights; this command never affects the vehicle itself (ADR 0003).
 */
export function LiveWatchButton({
  vehicleId,
  siteId,
  sites,
  hasDevice,
  className = "button button-secondary",
}: {
  vehicleId: string;
  siteId: string;
  sites: Site[];
  hasDevice: boolean;
  className?: string;
}) {
  const { user, tenant } = useSession();
  if (!hasDevice || !tenant || !canLiveWatchSite(user, tenant.id, siteId, sites)) return null;
  return <LiveWatchControl tenantId={tenant.id} vehicleId={vehicleId} className={className} />;
}

function LiveWatchControl({ tenantId, vehicleId, className }: { tenantId: string; vehicleId: string; className: string }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const w = useLiveWatch(provider, tenantId, vehicleId);
  if (w.waiting) {
    return (
      <span className="badge tone-info live-watch-active" role="status" aria-label={t("liveWatch.waitingLabel")} title={t("liveWatch.hint")}>
        <span className="spinner" aria-hidden="true" />
        {t("liveWatch.waiting")}
      </span>
    );
  }
  if (w.remainingS > 0) {
    const time = formatCountdown(w.remainingS);
    return (
      <span className="badge tone-ok live-watch-active" role="status" aria-label={t("liveWatch.activeLabel", { time })} title={t("liveWatch.hint")}>
        <span className="live-feed-dot is-live" aria-hidden="true" />
        {t("liveWatch.active", { time })}
      </span>
    );
  }
  return (
    <span className="live-watch">
      <button type="button" className={className} onClick={() => void w.start()} disabled={w.busy} title={t("liveWatch.hint")}>
        <Icon name="map" size={15} />
        {w.busy ? t("liveWatch.starting") : t("liveWatch.start")}
      </button>
      {w.error && (
        <span className="form-error live-watch-error" role="alert">
          {t(w.error)}
        </span>
      )}
    </span>
  );
}
